/**
 * CriteriaService
 * Handles fetching and updating classification criteria
 * 
 * Requirements: 5.1, 5.2, 5.4
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';
import type { Criterion, ValidationResult } from '../types';

export class CriteriaService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;

  constructor() {
    this.authService = AuthService.getInstance();

    // Create axios instance with base configuration
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000, // 10 second timeout
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include JWT token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          console.error('Authentication failed');
          await this.authService.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch user's criteria from backend
   * Requirement 5.1
   * 
   * @returns Promise resolving to array of criteria
   * @throws Error if fetch fails
   */
  async getCriteria(includeInactive = false): Promise<Criterion[]> {
    try {
      const params = includeInactive ? { include_inactive: 'true' } : {};
      const response = await this.axiosInstance.get<{ criteria: Criterion[] }>(
        '/api/criteria',
        { params }
      );

      console.log('Fetched criteria:', response.data.criteria.length);
      return response.data.criteria;
    } catch (error) {
      console.error('Failed to fetch criteria:', error);
      throw this.handleError(error, 'Failed to fetch criteria');
    }
  }

  /**
   * Update user's criteria
   * Requirement 5.4
   * 
   * @param criteria - Array of criteria to update
   * @returns Promise resolving to updated criteria
   * @throws Error if update fails or validation fails
   */
  async updateCriteria(criteria: Criterion[]): Promise<Criterion[]> {
    // Validate criteria before sending
    const validation = this.validateCriteria(criteria);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    try {
      const response = await this.axiosInstance.put<{ criteria: Criterion[] }>(
        '/api/criteria',
        { criteria }
      );

      console.log('Updated criteria successfully');
      return response.data.criteria;
    } catch (error) {
      console.error('Failed to update criteria:', error);
      throw this.handleError(error, 'Failed to update criteria');
    }
  }

  /**
   * Validate criteria structure
   * Requirement 5.2
   * 
   * @param criteria - Array of criteria to validate
   * @returns ValidationResult with valid flag and error messages
   */
  validateCriteria(criteria: Criterion[]): ValidationResult {
    const errors: string[] = [];

    // Check if criteria array exists
    if (!criteria || !Array.isArray(criteria)) {
      errors.push('Criteria must be an array');
      return { valid: false, errors };
    }

    // Check if at least one criterion exists
    if (criteria.length === 0) {
      errors.push('At least one criterion is required');
      return { valid: false, errors };
    }

    // Validate each criterion
    criteria.forEach((criterion, index) => {
      // Check required fields
      if (!criterion.name || criterion.name.trim() === '') {
        errors.push(`Criterion ${index + 1}: Name is required`);
      }

      if (!criterion.description || criterion.description.trim() === '') {
        errors.push(`Criterion ${index + 1}: Description is required`);
      }

      // Check categories
      if (!criterion.categories || !Array.isArray(criterion.categories)) {
        errors.push(`Criterion ${index + 1}: Categories must be an array`);
      } else {
        // Requirement 5.2: At least 2 categories required
        if (criterion.categories.length < 2) {
          errors.push(
            `Criterion ${index + 1} (${criterion.name}): At least 2 categories are required`
          );
        }

        // Validate category uniqueness
        const categoryNames = criterion.categories.map(cat => cat.name.toLowerCase());
        const uniqueNames = new Set(categoryNames);
        if (categoryNames.length !== uniqueNames.size) {
          errors.push(
            `Criterion ${index + 1} (${criterion.name}): Category names must be unique`
          );
        }

        // Validate each category
        criterion.categories.forEach((category, catIndex) => {
          if (!category.name || category.name.trim() === '') {
            errors.push(
              `Criterion ${index + 1} (${criterion.name}), Category ${catIndex + 1}: Name is required`
            );
          }

          if (!category.description || category.description.trim() === '') {
            errors.push(
              `Criterion ${index + 1} (${criterion.name}), Category ${catIndex + 1}: Description is required`
            );
          }

          // Validate price range
          if (typeof category.price_min !== 'number' || category.price_min < 0) {
            errors.push(
              `Criterion ${index + 1} (${criterion.name}), Category ${catIndex + 1}: Minimum price must be a non-negative number`
            );
          }

          if (typeof category.price_max !== 'number' || category.price_max < 0) {
            errors.push(
              `Criterion ${index + 1} (${criterion.name}), Category ${catIndex + 1}: Maximum price must be a non-negative number`
            );
          }

          if (category.price_min > category.price_max) {
            errors.push(
              `Criterion ${index + 1} (${criterion.name}), Category ${catIndex + 1}: Minimum price cannot be greater than maximum price`
            );
          }
        });
      }
    });

    // Validate criterion name uniqueness
    const criterionNames = criteria.map(c => c.name.toLowerCase());
    const uniqueCriterionNames = new Set(criterionNames);
    if (criterionNames.length !== uniqueCriterionNames.size) {
      errors.push('Criterion names must be unique');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Handle errors and convert to user-friendly messages
   * 
   * @param error - The error to handle
   * @param defaultMessage - Default message if error cannot be parsed
   * @returns Error with user-friendly message
   */
  private handleError(error: unknown, defaultMessage: string): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      if (axiosError.code === 'ECONNABORTED') {
        return new Error('Request timeout. Please check your connection.');
      }

      if (axiosError.code === 'ERR_NETWORK') {
        return new Error('Network error. Please check your connection.');
      }

      if (axiosError.response) {
        const status = axiosError.response.status;
        const data = axiosError.response.data as any;

        switch (status) {
          case 400:
            // Try to extract validation error from response
            // Backend can send errors in multiple formats
            if (data?.error?.message) {
              return new Error(data.error.message);
            }
            if (data?.detail) {
              return new Error(data.detail);
            }
            if (data?.message) {
              return new Error(data.message);
            }
            if (typeof data === 'string') {
              return new Error(data);
            }
            return new Error('Invalid criteria data. Please check your input and ensure you are within your subscription limits.');
          case 401:
            return new Error('Authentication failed. Please log in again.');
          case 403:
            return new Error('You do not have permission to modify criteria.');
          case 404:
            return new Error('Criteria not found.');
          case 429:
            return new Error('Too many requests. Please wait a moment.');
          case 500:
          case 502:
          case 503:
            return new Error('Server error. Please try again later.');
          default:
            if (data?.error?.message) {
              return new Error(data.error.message);
            }
            if (data?.detail) {
              return new Error(data.detail);
            }
            if (data?.message) {
              return new Error(data.message);
            }
            return new Error(`${defaultMessage} (Status: ${status})`);
        }
      }

      return new Error('Network error. Please check your connection.');
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error(defaultMessage);
  }
}

export default CriteriaService;
